import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { getReportNotes, createReportNote, addReportNoteReply, deleteReportNote, searchUsersForMentions } from '../../utils/api';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import { formatRelativeTime } from '../Notes/noteHelpers';
import { CATEGORY_CONFIG, SEVERITY_DOT } from './NightAuditAlerts';

// ---------------------------------------------------------------------------
// Color hash for author avatar (matches NoteCard pattern)
// ---------------------------------------------------------------------------
const AVATAR_COLORS = [
  ['#3b82f6', '#1d4ed8'], // blue
  ['#ec4899', '#be185d'], // pink
  ['#10b981', '#047857'], // green
  ['#f59e0b', '#b45309'], // amber
  ['#8b5cf6', '#6d28d9'], // purple
  ['#06b6d4', '#0e7490'], // cyan
  ['#f43f5e', '#be123c'], // rose
];

function getAuthorColor(name) {
  const hash = (name || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function getInitials(name) {
  if (!name) return '??';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

// ---------------------------------------------------------------------------
// AlertNotesModal
// ---------------------------------------------------------------------------
export default function AlertNotesModal({ flag, orgId, onClose, onNoteCountChange, initialNotes }) {
  const [notes, setNotes] = useState(() => {
    if (initialNotes?.length) {
      return [...initialNotes].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    }
    return [];
  });
  const [loading, setLoading] = useState(!initialNotes?.length);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // noteId being replied to
  const threadRef = useRef(null);
  const inputRef = useRef(null);

  // Mention state
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionPosition, setMentionPosition] = useState(0);
  const [mentionMapping, setMentionMapping] = useState({}); // name -> email
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const mentionDropdownRef = useRef(null);

  const { userSettings, currentOrganization } = useUserSettingsContext();
  const currentUserId = userSettings?.email;
  const currentUserName = userSettings?.displayName || userSettings?.email || 'Unknown';

  const [showDetails, setShowDetails] = useState(false);

  const catConfig = CATEGORY_CONFIG[flag.category] || { label: flag.category?.toUpperCase(), bg: '#F5F5F5', color: '#424242' };
  const dotColor = SEVERITY_DOT[flag.severity] || SEVERITY_DOT.low;

  // Filter users based on mention query
  const filteredUsers = showMentions ? users.filter(user =>
    !mentionQuery || mentionQuery === '' || (
      user.name.toLowerCase().includes(mentionQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(mentionQuery.toLowerCase())
    )
  ) : [];

  // Load users for mention autocomplete
  const loadUsers = useCallback(async (query = '') => {
    if (loadingUsers) return;
    setLoadingUsers(true);
    try {
      const orgCode = currentOrganization?.code;
      const orgIdVal = currentOrganization?.id;
      const orgUsers = await searchUsersForMentions(query, orgCode, orgIdVal);
      setUsers(orgUsers);
    } catch (err) {
      console.error('Error loading users for mentions:', err);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [currentOrganization?.code, currentOrganization?.id]);

  // Load users on mount
  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Load notes
  const loadNotes = useCallback(async () => {
    try {
      const res = await getReportNotes({
        orgId,
        reportId: 'night-audit-alerts',
        pageId: String(flag.id),
      });
      const loaded = (res.notes || [])
        .filter(n => !n.isDeleted)
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      setNotes(loaded);
    } catch (err) {
      console.error('Failed to load notes', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, flag.id]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  // Auto-scroll to bottom when notes change
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [notes]);

  // Focus input on mount
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Close on Escape (only when mention dropdown is not showing)
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && !showMentions) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, showMentions]);

  // Scroll selected mention into view
  useEffect(() => {
    if (showMentions && mentionDropdownRef.current) {
      const el = mentionDropdownRef.current.children[selectedMentionIndex];
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedMentionIndex, showMentions]);

  // Extract mentions from text using mentionMapping
  const extractMentions = (text) => {
    const mentions = [];
    // Check for each known mention name in the text
    for (const [name, email] of Object.entries(mentionMapping)) {
      const pattern = `@${name}`;
      let startIndex = 0;
      while (true) {
        const pos = text.indexOf(pattern, startIndex);
        if (pos === -1) break;
        mentions.push({ name, position: pos, email });
        startIndex = pos + pattern.length;
      }
    }
    return mentions;
  };

  // Handle text change with @mention detection
  const handleTextChange = (e) => {
    const text = e.target.value;
    const cursorPosition = e.target.selectionStart;
    setInputText(text);

    const textBeforeCursor = text.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1 && cursorPosition - lastAtIndex <= 30) {
      const query = text.substring(lastAtIndex + 1, cursorPosition);
      // Check if this @ already has a completed mention (name followed by space then more text)
      // A completed mention looks like "@Name " — if user selected from dropdown, the name is in mentionMapping
      const spaceIndex = query.indexOf(' ');
      const nameBeforeSpace = spaceIndex !== -1 ? query.substring(0, spaceIndex) : null;
      const isCompletedMention = nameBeforeSpace && mentionMapping[nameBeforeSpace];
      // Also check for multi-word completed mentions (e.g. "Derek Gay")
      const isCompletedMultiWord = spaceIndex !== -1 && Object.keys(mentionMapping).some(name =>
        query.startsWith(name + ' ') && query.length > name.length + 1
      );

      if (!query.includes('\n') && !isCompletedMention && !isCompletedMultiWord) {
        setMentionQuery(query);
        setMentionPosition(lastAtIndex);
        setShowMentions(true);
        setSelectedMentionIndex(0);
        loadUsers(query);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  // Handle mention selection
  const handleMentionSelect = (user) => {
    const beforeMention = inputText.substring(0, mentionPosition);
    const afterMention = inputText.substring(mentionPosition + mentionQuery.length + 1);
    const newText = `${beforeMention}@${user.name} ${afterMention}`;
    const newCursorPosition = mentionPosition + user.name.length + 2;

    setMentionMapping(prev => ({ ...prev, [user.name]: user.email }));
    setInputText(newText);
    setShowMentions(false);
    setMentionQuery('');

    if (inputRef.current) {
      inputRef.current.focus();
      setTimeout(() => {
        inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
    }
  };

  // Send note or reply (optimistic — show immediately, persist in background)
  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || sending) return;

    const mentions = extractMentions(text);
    const tempId = `temp-${Date.now()}`;
    const now = new Date().toISOString();
    const targetNoteId = replyingTo; // capture before clearing

    // Optimistically update UI
    if (targetNoteId) {
      const optimisticReply = { id: tempId, userDisplayName: currentUserName, userId: currentUserId, replyText: text, createdAt: now };
      setNotes(prev => prev.map(n =>
        n.id === targetNoteId ? { ...n, replies: [...(n.replies || []), optimisticReply] } : n
      ));
    } else {
      const optimisticNote = { id: tempId, userDisplayName: currentUserName, userId: currentUserId, noteText: text, createdAt: now, replies: [] };
      setNotes(prev => [...prev, optimisticNote]);
    }
    setInputText('');
    setReplyingTo(null);
    setMentionMapping({});
    onNoteCountChange?.();

    // Persist in background
    setSending(true);
    try {
      if (targetNoteId) {
        await addReportNoteReply(targetNoteId, orgId, {
          userId: currentUserId,
          userDisplayName: currentUserName,
          replyText: text,
          mentions,
        });
      } else {
        await createReportNote({
          orgId: parseInt(orgId),
          locationId: flag.locationId,
          locationName: flag.hotelName || '',
          reportId: 'night-audit-alerts',
          pageId: String(flag.id),
          noteText: text,
          dataContext: `Night Audit Alert: ${flag.title}`,
          userDisplayName: currentUserName,
          author: { id: currentUserId, name: currentUserName, email: currentUserId },
          mentions,
          noteUrl: `/dashboard/night-audit?flagId=${flag.id}`,
        });
      }
      // Reload to swap temp IDs for real ones
      await loadNotes();
    } catch (err) {
      console.error('Failed to send note', err);
      // Roll back optimistic entry
      setNotes(prev => prev
        .filter(n => n.id !== tempId)
        .map(n => ({ ...n, replies: (n.replies || []).filter(r => r.id !== tempId) }))
      );
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    // Handle mention dropdown navigation
    if (showMentions && filteredUsers.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedMentionIndex(prev => prev < filteredUsers.length - 1 ? prev + 1 : 0);
          return;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : filteredUsers.length - 1);
          return;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          handleMentionSelect(filteredUsers[selectedMentionIndex]);
          return;
        case 'Escape':
          e.preventDefault();
          setShowMentions(false);
          setMentionQuery('');
          return;
        default:
          break;
      }
    }

    // Default: Enter to send
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Delete a note (optimistic)
  const handleDelete = async (noteId) => {
    const prev = notes;
    setNotes(n => n.filter(note => note.id !== noteId));
    onNoteCountChange?.();
    try {
      await deleteReportNote(noteId, orgId);
      await loadNotes();
    } catch (err) {
      console.error('Failed to delete note', err);
      setNotes(prev); // rollback
    }
  };

  // Flatten notes + replies into a chronological thread for display
  const threadItems = [];
  for (const note of notes) {
    const authorId = note.userId || note.author?.id || note.author?.email;
    threadItems.push({ type: 'note', id: note.id, userId: authorId, author: note.userDisplayName || note.author?.name || note.userId, text: note.noteText, createdAt: note.createdAt, noteId: note.id });
    if (note.replies?.length) {
      for (const reply of note.replies) {
        threadItems.push({ type: 'reply', id: reply.id, author: reply.userDisplayName || reply.userId, text: reply.replyText, createdAt: reply.createdAt, parentNoteId: note.id });
      }
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: 1000,
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%', maxWidth: 580,
        maxHeight: '85vh',
        background: 'white',
        borderRadius: 16,
        boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
        zIndex: 1001,
        display: 'flex', flexDirection: 'column',
        fontFamily: 'var(--font-sans)',
      }}>

        {/* Header — mirrors AlertRow styling */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--lodge-onyx-100)',
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          {/* Severity dot */}
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: dotColor, flexShrink: 0, marginTop: 6,
          }} />

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--lodge-onyx-800)', lineHeight: 1.3 }}>
                {flag.title}
              </span>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.5px',
                padding: '2px 6px', borderRadius: 'var(--radius-sm)',
                background: catConfig.bg, color: catConfig.color,
              }}>
                {catConfig.label}
              </span>
            </div>
            {flag.hotelName && (
              <div style={{ fontSize: 12, color: 'var(--lodge-onyx-400)', marginTop: 2 }}>
                {flag.hotelName}
                {flag.severityScore && (
                  <span style={{ marginLeft: 8, fontWeight: 600, color: flag.severity === 'high' ? '#F04438' : flag.severity === 'medium' ? '#F79009' : 'var(--lodge-onyx-500)' }}>
                    Score: {flag.severityScore}
                  </span>
                )}
                {flag.recurrenceCount > 1 && (
                  <span style={{ marginLeft: 8 }}>{flag.recurrenceCount} nights</span>
                )}
              </div>
            )}

            {/* Toggle details */}
            <button
              onClick={() => setShowDetails(v => !v)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 11, color: 'var(--lodge-onyx-400)', padding: '2px 0', marginTop: 4,
                display: 'inline-flex', alignItems: 'center', gap: 3,
              }}
            >
              {showDetails ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              {showDetails ? 'Hide details' : 'Show details'}
            </button>
          </div>

          {/* Close */}
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 4, color: 'var(--lodge-onyx-400)', flexShrink: 0,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Expandable details */}
        {showDetails && (
          <div style={{
            padding: '12px 20px',
            borderBottom: '1px solid var(--lodge-onyx-100)',
            background: 'var(--lodge-onyx-50, #F9FAFB)',
            fontSize: 13, color: 'var(--lodge-onyx-700)', lineHeight: 1.5,
            maxHeight: 200, overflowY: 'auto',
          }}>
            {flag.description && (
              <p style={{ margin: '0 0 8px' }}>{flag.description}</p>
            )}
            {flag.dataPoints?.length > 0 && (
              <ul style={{ margin: '0 0 8px', paddingLeft: 16, fontSize: 12, color: 'var(--lodge-onyx-600)', lineHeight: 1.6 }}>
                {flag.dataPoints.map((pt, i) => <li key={i}>{pt}</li>)}
              </ul>
            )}
            {flag.recommendedAction && (
              <p style={{ margin: 0, fontSize: 12, color: 'var(--lodge-onyx-500)', fontStyle: 'italic' }}>
                <strong style={{ fontStyle: 'normal', color: 'var(--lodge-onyx-700)' }}>Action: </strong>
                {flag.recommendedAction}
              </p>
            )}
            {flag.auditDate && (
              <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--lodge-onyx-400)' }}>
                Audit date: {new Date(flag.auditDate).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
            )}
          </div>
        )}

        {/* Thread area */}
        <div
          ref={threadRef}
          style={{
            flex: 1, overflowY: 'auto',
            padding: '16px 20px',
            background: 'var(--lodge-onyx-50, #F9FAFB)',
            display: 'flex', flexDirection: 'column', gap: 12,
            minHeight: 200,
          }}
        >
          {loading && (
            <div style={{ textAlign: 'center', color: 'var(--lodge-onyx-400)', fontSize: 13, padding: 40 }}>
              Loading notes...
            </div>
          )}

          {!loading && threadItems.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--lodge-onyx-400)', fontSize: 13, padding: 40 }}>
              No notes yet. Start the conversation below.
            </div>
          )}

          {threadItems.map((item) => {
            const colors = getAuthorColor(item.author);
            const isReply = item.type === 'reply';

            return (
              <div
                key={item.id}
                style={{
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                  marginLeft: isReply ? 32 : 0,
                }}
              >
                {/* Avatar */}
                <div style={{
                  width: isReply ? 26 : 32, height: isReply ? 26 : 32,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  color: 'white', fontSize: isReply ? 10 : 11, fontWeight: 700,
                }}>
                  {getInitials(item.author)}
                </div>

                {/* Bubble */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--lodge-onyx-700)' }}>
                      {item.author}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--lodge-onyx-400)' }}>
                      {formatRelativeTime(item.createdAt)}
                    </span>
                  </div>
                  <div style={{
                    background: 'white',
                    border: '1px solid var(--lodge-onyx-100)',
                    borderRadius: '2px 10px 10px 10px',
                    padding: '8px 12px',
                    fontSize: 13, color: 'var(--lodge-onyx-700)',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}>
                    {item.text}
                  </div>
                  {/* Actions for top-level notes */}
                  {!isReply && (
                    <div style={{ display: 'flex', gap: 10, marginTop: 2 }}>
                      <button
                        onClick={() => { setReplyingTo(item.noteId); inputRef.current?.focus(); }}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: 11, color: 'var(--lodge-onyx-400)', padding: '2px 0',
                        }}
                      >
                        Reply
                      </button>
                      {item.userId === currentUserId && (
                        <button
                          onClick={() => handleDelete(item.noteId)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            fontSize: 11, color: 'var(--lodge-onyx-300)', padding: '2px 0',
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                          }}
                          onMouseEnter={e => e.currentTarget.style.color = 'var(--color-error, #F04438)'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--lodge-onyx-300)'}
                        >
                          <Trash2 size={10} />
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Input area */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid var(--lodge-onyx-100)',
          background: 'white',
          borderRadius: '0 0 16px 16px',
          position: 'relative',
        }}>
          {replyingTo && (
            <div style={{
              fontSize: 11, color: 'var(--lodge-onyx-400)', marginBottom: 6,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              Replying to note
              <button
                onClick={() => setReplyingTo(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--lodge-onyx-400)', fontSize: 11 }}
              >
                (cancel)
              </button>
            </div>
          )}

          {/* Mention dropdown — positioned above input */}
          {showMentions && (
            <div
              ref={mentionDropdownRef}
              style={{
                position: 'absolute',
                bottom: '100%',
                left: 20, right: 20,
                background: 'white',
                border: '1px solid var(--lodge-onyx-200)',
                borderRadius: 8,
                boxShadow: '0 -4px 16px rgba(0,0,0,0.1)',
                maxHeight: 200,
                overflowY: 'auto',
                zIndex: 10,
                marginBottom: 4,
              }}
            >
              {filteredUsers.length > 0 ? filteredUsers.slice(0, 10).map((user, index) => {
                const colors = getAuthorColor(user.name);
                return (
                  <div
                    key={user.id}
                    onClick={() => handleMentionSelect(user)}
                    onMouseEnter={() => setSelectedMentionIndex(index)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px',
                      cursor: 'pointer',
                      background: index === selectedMentionIndex ? 'var(--lodge-onyx-50, #F9FAFB)' : 'transparent',
                      borderBottom: index < filteredUsers.length - 1 && index < 9 ? '1px solid var(--lodge-onyx-50, #F9FAFB)' : 'none',
                    }}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                      color: 'white', fontSize: 10, fontWeight: 700,
                    }}>
                      {getInitials(user.name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--lodge-onyx-700)' }}>
                        {user.name}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--lodge-onyx-400)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {user.email}
                      </div>
                    </div>
                  </div>
                );
              }) : (
                <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--lodge-onyx-400)', textAlign: 'center' }}>
                  {loadingUsers ? 'Loading users...' :
                   mentionQuery ? `No users found for "${mentionQuery}"` :
                   'No users available'}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea
              ref={inputRef}
              value={inputText}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              placeholder={replyingTo ? 'Write a reply... (@ to mention)' : 'Add a note... (@ to mention)'}
              rows={1}
              style={{
                flex: 1,
                resize: 'none',
                border: '1px solid var(--lodge-onyx-200)',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 13,
                fontFamily: 'var(--font-sans)',
                lineHeight: 1.5,
                outline: 'none',
                minHeight: 38,
                maxHeight: 120,
                overflowY: 'auto',
              }}
              onInput={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
            />
            <button
              onClick={handleSend}
              disabled={!inputText.trim() || sending}
              style={{
                background: inputText.trim() ? 'var(--lodge-onyx-800)' : 'var(--lodge-onyx-200)',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                width: 38, height: 38,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: inputText.trim() ? 'pointer' : 'default',
                flexShrink: 0,
                transition: 'background 0.15s',
              }}
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
